import { useState } from "react";
import { heatCurve, heatHours, rankTabs } from "@/mocks/home";

const trendStyle: Record<string, string> = {
  up: "text-primary-600",
  down: "text-secondary-600",
  same: "text-foreground-500",
};

const trendIcon: Record<string, string> = {
  up: "ri-arrow-up-line",
  down: "ri-arrow-down-line",
  same: "ri-subtract-line",
};

export default function HotRanking() {
  const [activeKey, setActiveKey] = useState(rankTabs[0].key);
  const current = rankTabs.find((tab) => tab.key === activeKey) ?? rankTabs[0];
  const maxHeat = Math.max(...heatCurve);

  return (
    <section id="rank" className="w-full border-b border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-page px-4 py-8 md:px-6 md:py-12">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs text-primary-700">
              <i className="ri-fire-fill text-primary-500" />
              热瓜榜
            </span>
            <h2 className="mt-3 font-heading text-3xl leading-tight text-foreground-950 md:text-4xl">
              今日吃瓜指数
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">
              按讨论量、转载速度与社区投票实时排位，涨跌都留着痕迹，热度退了自然下沉。
            </p>
          </div>
          <div className="inline-flex w-fit items-center gap-1 rounded-full border border-background-300 bg-background-50 px-1 py-1">
            {rankTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveKey(tab.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors md:px-4 ${
                  activeKey === tab.key
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 lg:col-span-7">
            <ul>
              {current.items.map((item) => (
                <li
                  key={`${current.key}-${item.rank}`}
                  className="flex items-center gap-3 border-b border-background-200 py-3.5 last:border-b-0 md:gap-4"
                >
                  <span
                    className={`w-7 shrink-0 text-center font-heading text-xl ${
                      item.rank <= 3 ? "text-primary-600" : "text-background-500"
                    }`}
                  >
                    {item.rank}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-1 text-sm text-foreground-900 md:text-base">
                      {item.title}
                    </p>
                    <div className="mt-2 flex items-center gap-2.5">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-200">
                        <div
                          className="h-full rounded-full bg-primary-500"
                          style={{ width: `${item.heat}%` }}
                        />
                      </div>
                      <span className="w-9 shrink-0 text-right text-xs text-foreground-500">
                        {item.heat}
                      </span>
                    </div>
                  </div>
                  <span
                    className={`flex w-14 shrink-0 items-center justify-end gap-0.5 text-xs ${trendStyle[item.trend]}`}
                  >
                    <i className={trendIcon[item.trend]} />
                    {item.delta}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 lg:col-span-5">
            <div className="flex items-center justify-between gap-3">
              <h3 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                <i className="ri-line-chart-line text-accent-600" />
                24 小时热度曲线
              </h3>
              <span className="flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-1 text-xs text-accent-900">
                <i className="ri-arrow-up-line" />
                峰值 10:00
              </span>
            </div>

            <div className="mt-5 flex h-40 items-stretch gap-1.5 md:h-48 md:gap-2">
              {heatCurve.map((value, index) => (
                <div key={heatHours[index]} className="flex h-full flex-1 flex-col">
                  <div className="flex flex-1 items-end">
                    <div
                      className={`animate-bar w-full rounded-t-md ${
                        value === maxHeat ? "bg-primary-500" : "bg-secondary-300"
                      }`}
                      style={{ height: `${value}%`, animationDelay: `${index * 0.05}s` }}
                    />
                  </div>
                  <span className="mt-2 text-center text-[10px] text-foreground-400">
                    {heatHours[index]}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-background-200 pt-3.5 text-xs text-foreground-600">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-primary-500" />
                峰值时段
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-secondary-300" />
                常规时段
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-background-300" />
                每小时采样
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}